<template>
  <div class="inline-flex items-center gap-1">
    <span
      v-for="(segment, index) in segments"
      :key="index"
      class="inline-flex min-w-6 h-6 items-center justify-center rounded border border-[#ebeef5] bg-white px-1.5 text-xs font-medium text-label"
    >
      {{ segment }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { ModifierKeys } from "@/enums/keyboardShortcuts";
import { isMacOs } from "@/hooks/keyboard/normalizeShortcut";

import type { UserKeyboardShortcut } from "@/models/options";

const { shortcut } = defineProps<{ shortcut: Partial<UserKeyboardShortcut> }>();

const MODIFIER_LABELS: Record<string, string> = {
  [ModifierKeys.Meta]: isMacOs ? "\u2318" : "Ctrl",
  [ModifierKeys.Shift]: "\u21E7",
  [ModifierKeys.Alt]: isMacOs ? "\u2325" : "Alt",
  [ModifierKeys.Ctrl]: "Ctrl",
};

const KEY_LABELS: Record<string, string> = {
  ArrowLeft: "\u2190",
  ArrowRight: "\u2192",
  ArrowUp: "\u2191",
  ArrowDown: "\u2193",
  "\\": "\\",
  "/": "/",
  " ": "Space",
};

// macOS Option key produces Unicode characters instead of the actual key.
// This map reverses them back to the physical key for display.
const MAC_OPTION_KEY_MAP: Record<string, string> = {
  å: "A",
  "\u222B": "B",
  ç: "C",
  "\u2202": "D",
  "\u00B4": "E",
  ƒ: "F",
  "\u00A9": "G",
  "\u02D9": "H",
  ˆ: "I",
  "\u2206": "J",
  "\u02DA": "K",
  "\u00AC": "L",
  µ: "M",
  "\u02DC": "N",
  ø: "O",
  π: "P",
  œ: "Q",
  "\u00AE": "R",
  ß: "S",
  "\u2020": "T",
  "\u00A8": "U",
  "\u221A": "V",
  "\u2211": "W",
  "\u2248": "X",
  "\u00A5": "Y",
  Ω: "Z",
  "\u00A1": "1",
  "\u2122": "2",
  "\u00A3": "3",
  "\u00A2": "4",
  "\u221E": "5",
  "\u00A7": "6",
  "\u00B6": "7",
  "\u2022": "8",
  ª: "9",
  º: "0",
  "\u2013": "-",
  "\u2260": "=",
  "\u2264": ",",
  "\u2265": ".",
  "\u00F7": "/",
  "\u201C": "[",
  "\u2018": "]",
  "\u00AB": "\\",
  æ: "'",
  "\u2026": ";",
  // Option+Shift combos
  "\u00BF": "?",
  Î: "D",
  ω: "Z",
  Â: "M",
};

const hasAltModifier = (modifiers: ModifierKeys[] | undefined): boolean => {
  return (modifiers ?? []).includes(ModifierKeys.Alt);
};

const getKeyLabel = (key: string, useOptionMap: boolean): string => {
  if (KEY_LABELS[key]) {
    return KEY_LABELS[key];
  }
  if (useOptionMap && MAC_OPTION_KEY_MAP[key]) {
    return MAC_OPTION_KEY_MAP[key];
  }
  if (key.length === 1) {
    return key.toUpperCase();
  }
  return key;
};

const segments = computed(() => {
  const parts: string[] = [];
  const useOptionMap = isMacOs && hasAltModifier(shortcut.modifiers);

  for (const modifier of shortcut.modifiers ?? []) {
    const label = MODIFIER_LABELS[modifier];
    if (label) {
      parts.push(label);
    }
  }

  for (const key of shortcut.keys ?? []) {
    parts.push(getKeyLabel(key, useOptionMap));
  }

  return parts;
});
</script>
